/**
 * Automation tabs for AdminPanel — split into a single file so AdminPanel
 * stays scannable. Each component is self-contained and consumes /api/admin/*.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Inbox, AlertCircle, CheckCircle2, RefreshCw, Search, FileText, Bell,
  ShieldCheck, Activity, Mail, Phone, Clock, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_STYLES = {
  open:           "bg-amber-50 text-amber-700 border-amber-200",
  in_progress:    "bg-blue-50 text-blue-700 border-blue-200",
  completed:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed:         "bg-slate-100 text-slate-700 border-slate-200",
  new:            "bg-amber-50 text-amber-700 border-amber-200",
  matched:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:       "bg-red-50 text-trital-red border-red-200",
  needs_more_info:"bg-blue-50 text-blue-700 border-blue-200",
  queued:         "bg-amber-50 text-amber-700 border-amber-200",
  sent:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:         "bg-red-50 text-trital-red border-red-200",
};
const PRIORITY_STYLES = {
  low:    "bg-slate-100 text-slate-700",
  normal: "bg-blue-50 text-blue-700",
  urgent: "bg-red-50 text-trital-red",
};

/* CSV download helper */
export function downloadCSV(rows, filename) {
  if (!rows || rows.length === 0) return toast.error("Nothing to export");
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r || {}).forEach((k) => s.add(k)); return s; }, new Set()));
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") v = JSON.stringify(v);
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
  toast.success("CSV downloaded");
}

/* ============================================================== */
/*  TASKS TAB                                                      */
/* ============================================================== */
export function TasksTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");
  const [priority, setPriority] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status)   params.status   = status;
      if (priority) params.priority = priority;
      if (type)     params.task_type = type;
      const r = await api.get("/recruiter-tasks", { params });
      setItems(r.data || []);
    } catch (e) { toast.error("Couldn't load tasks", { description: e?.response?.data?.detail || e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, priority, type]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const ql = q.toLowerCase();
    return items.filter((t) =>
      (t.title || "").toLowerCase().includes(ql) ||
      (t.summary || "").toLowerCase().includes(ql) ||
      (t.assignee_role || "").includes(ql),
    );
  }, [q, items]);

  const setStatusOf = async (id, s) => {
    try { await api.put(`/recruiter-tasks/${id}`, { status: s }); toast.success(`Task → ${s}`); load(); }
    catch (e) { toast.error("Couldn't update task", { description: e?.response?.data?.detail || e.message }); }
  };
  const setPriorityOf = async (id, p) => {
    try { await api.put(`/recruiter-tasks/${id}`, { priority: p }); toast.success(`Priority → ${p}`); load(); }
    catch (e) { toast.error("Couldn't update task", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div className="space-y-4" data-testid="admin-tasks-tab">
      <div className="rounded-2xl bg-white border border-slate-200 shadow-card p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.8} />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title / summary / role"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:border-trital-navy"
            data-testid="tasks-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="tasks-filter-status">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="closed">Closed</option>
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="tasks-filter-priority">
          <option value="">Any priority</option>
          <option value="urgent">Urgent</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="tasks-filter-type">
          <option value="">All types</option>
          <option value="new_clinician_lead">Clinician lead</option>
          <option value="new_facility_lead">Facility lead</option>
          <option value="staffing_request_follow_up">Staffing request</option>
          <option value="shift_application_review">Shift app</option>
          <option value="checklist_review">Checklist</option>
          <option value="compliance_follow_up">Compliance</option>
        </select>
        <button onClick={load} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px] inline-flex items-center gap-1.5 hover:border-trital-navy" data-testid="tasks-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} /> Refresh
        </button>
        <button onClick={() => downloadCSV(filtered, "recruiter-tasks")} className="ml-auto px-3 py-2 rounded-lg bg-trital-navy text-white text-[12.5px] font-semibold hover:bg-trital-navy-deep" data-testid="tasks-export-csv">
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">Loading tasks…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No tasks match this filter.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.1) }}
              className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid lg:grid-cols-[1.6fr_1fr_auto] gap-4 items-start"
              data-testid={`task-row-${t.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[t.status] || ""}`}>{(t.status || "open").replace("_", " ")}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${PRIORITY_STYLES[t.priority] || ""}`}>{t.priority}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{(t.task_type || "").replace(/_/g, " ")}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider">→ {t.assignee_role}</span>
                </div>
                <div className="mt-2 font-display font-bold text-slate-900">{t.title}</div>
                {t.summary && <p className="mt-1.5 text-[12.5px] text-slate-600 line-clamp-2">{t.summary}</p>}
                <div className="mt-1.5 text-[11px] text-slate-400">{t.created_at?.slice(0, 16).replace("T", " ")} · ref={t.ref_type}/{t.ref_id?.slice(0, 8)}</div>
              </div>
              <div className="space-y-1.5 text-[12px] text-slate-500">
                {t.meta?.email && (<div className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {t.meta.email}</div>)}
                {t.meta?.phone && (<div><a href={`tel:${t.meta.phone}`} className="inline-flex items-center gap-1.5 hover:text-trital-navy"><Phone className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {t.meta.phone}</a></div>)}
                {t.meta?.specialty && <div className="text-[11px]">Specialty: <span className="font-semibold text-slate-700">{t.meta.specialty}</span></div>}
                {t.meta?.timeline && <div className="text-[11px]">Timeline: <span className="font-semibold text-slate-700">{t.meta.timeline}</span></div>}
              </div>
              <div className="flex flex-col gap-1.5 lg:items-end">
                <select value={t.status} onChange={(e) => setStatusOf(t.id, e.target.value)}
                  className="px-3 py-1.5 rounded-full text-[11.5px] font-semibold border border-slate-200 bg-white focus:outline-none focus:border-trital-navy"
                  data-testid={`task-status-${t.id}`}>
                  <option value="open">open</option>
                  <option value="in_progress">in progress</option>
                  <option value="completed">completed</option>
                  <option value="closed">closed</option>
                </select>
                <select value={t.priority} onChange={(e) => setPriorityOf(t.id, e.target.value)}
                  className="px-3 py-1.5 rounded-full text-[11.5px] font-semibold border border-slate-200 bg-white focus:outline-none focus:border-trital-navy"
                  data-testid={`task-priority-${t.id}`}>
                  <option value="low">low</option>
                  <option value="normal">normal</option>
                  <option value="urgent">urgent</option>
                </select>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/*  STAFFING REQUESTS TAB                                          */
/* ============================================================== */
export function StaffingRequestsTab() {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("");
  const [urgencyF, setUrgencyF] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF)  params.status  = statusF;
      if (urgencyF) params.urgency = urgencyF;
      const r = await api.get("/admin/staffing-requests", { params });
      setItems(r.data || []);
    } catch (e) { toast.error("Couldn't load staffing requests", { description: e?.response?.data?.detail || e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, urgencyF]);

  const setStatus = async (id, s) => {
    try { await api.post(`/admin/staffing-requests/${id}/status`, { status: s }); toast.success(`Request → ${s}`); load(); }
    catch (e) { toast.error("Couldn't update request", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div className="space-y-4" data-testid="admin-staffing-tab">
      <div className="rounded-2xl bg-white border border-slate-200 shadow-card p-3 flex flex-wrap items-center gap-2">
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="staffing-filter-status">
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="in_progress">In progress</option>
          <option value="matched">Matched</option>
          <option value="closed">Closed</option>
        </select>
        <select value={urgencyF} onChange={(e) => setUrgencyF(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="staffing-filter-urgency">
          <option value="">Any urgency</option>
          <option value="urgent">Urgent</option>
          <option value="normal">Normal</option>
        </select>
        <span className="ml-auto text-[12px] text-slate-500">{items.length} requests</span>
        <button onClick={() => downloadCSV(items, "staffing-requests")} className="px-3 py-2 rounded-lg bg-trital-navy text-white text-[12.5px] font-semibold hover:bg-trital-navy-deep" data-testid="staffing-export-csv">
          Export CSV
        </button>
      </div>
      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No staffing requests match.</div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid lg:grid-cols-[1.4fr_1fr_auto] gap-4 items-start" data-testid={`staffing-row-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[r.status] || ""}`}>{r.status || "new"}</span>
                  {r.urgency === "urgent" && <span className="inline-flex px-2 py-0.5 rounded-full bg-red-50 text-trital-red border border-red-200 text-[10px] uppercase tracking-wider font-bold">Urgent</span>}
                  {r.specialty && <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{r.specialty}</span>}
                </div>
                <div className="mt-2 font-display font-bold text-slate-900">{r.organization || r.name}</div>
                <div className="text-[12.5px] text-slate-600">{r.name} · {r.title || "—"}</div>
                {r.need && <p className="mt-1.5 text-[12.5px] text-slate-500 line-clamp-2">{r.need}</p>}
                <div className="mt-1.5 text-[11px] text-slate-400">{r.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="space-y-1.5 text-[12.5px]">
                {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-trital-navy"><Mail className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {r.email}</a>}
                {r.phone && <div><a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-trital-navy"><Phone className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {r.phone}</a></div>}
                {r.timeline && <div className="text-[11px] text-slate-500">Timeline: <span className="font-semibold text-slate-700">{r.timeline}</span></div>}
              </div>
              <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
                className="px-3 py-1.5 rounded-full text-[12px] font-semibold border border-slate-200 bg-white focus:outline-none focus:border-trital-navy"
                data-testid={`staffing-status-${r.id}`}>
                <option value="new">new</option>
                <option value="in_progress">in progress</option>
                <option value="matched">matched</option>
                <option value="closed">closed</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/*  CHECKLIST SUBMISSIONS TAB                                      */
/* ============================================================== */
export function ChecklistSubmissionsTab() {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("pending_review");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/checklist-submissions", { params: statusF ? { status: statusF } : {} });
      setItems(r.data || []);
    } catch (e) { toast.error("Couldn't load checklists", { description: e?.response?.data?.detail || e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

  const setStatus = async (id, s) => {
    try { await api.post(`/admin/checklist-submissions/${id}/status`, { status: s }); toast.success(`Checklist → ${s}`); load(); }
    catch (e) { toast.error("Couldn't update checklist", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div className="space-y-4" data-testid="admin-checklists-tab">
      <div className="rounded-2xl bg-white border border-slate-200 shadow-card p-3 flex flex-wrap items-center gap-2">
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="checklists-filter">
          <option value="">All statuses</option>
          <option value="pending_review">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="needs_more_info">Needs more info</option>
        </select>
        <span className="ml-auto text-[12px] text-slate-500">{items.length} submissions</span>
        <button onClick={() => downloadCSV(items, "checklist-submissions")} className="px-3 py-2 rounded-lg bg-trital-navy text-white text-[12.5px] font-semibold hover:bg-trital-navy-deep" data-testid="checklists-export-csv">
          Export CSV
        </button>
      </div>
      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No submissions in this view.</div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid lg:grid-cols-[1.4fr_1fr_auto] gap-4 items-start" data-testid={`checklist-row-${s.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[s.status] || ""}`}>{(s.status || "pending_review").replace("_", " ")}</span>
                  {s.specialty && <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{s.specialty}</span>}
                  {typeof s.completion_pct === "number" && <span className="text-[11px] text-slate-500">{s.completion_pct}% complete</span>}
                  {s.email_status && (
                    <span
                      data-testid={`checklist-email-status-${s.id}`}
                      className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${
                        s.email_status === "sent"   ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        s.email_status === "failed" ? "bg-red-50 text-red-700 border-red-200" :
                                                       "bg-amber-50 text-amber-700 border-amber-200"
                      }`}
                    >
                      Email · {s.email_status}
                    </span>
                  )}
                  {s.email_provider && s.email_provider !== "none" && (
                    <span
                      data-testid={`checklist-email-provider-${s.id}`}
                      className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${
                        s.email_provider === "microsoft365" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                        s.email_provider === "resend"       ? "bg-purple-50 text-purple-700 border-purple-200" :
                                                              "bg-slate-50 text-slate-600 border-slate-200"
                      }`}
                    >
                      {s.email_provider === "microsoft365" ? "via M365" : `via ${s.email_provider}`}
                    </span>
                  )}
                  {s.fallback_used && (
                    <span
                      data-testid={`checklist-fallback-${s.id}`}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-bold uppercase tracking-wider"
                      title="Microsoft 365 failed; delivered via Resend fallback"
                    >
                      Fallback
                    </span>
                  )}
                  {s.pdf_status === "generated" && (
                    <a
                      href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/admin/checklist-submissions/${s.id}/pdf`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-[10px] px-2 py-0.5 rounded-full bg-trital-navy/10 text-trital-navy font-bold uppercase tracking-wider hover:bg-trital-navy hover:text-white border border-trital-navy/20"
                      data-testid={`checklist-pdf-${s.id}`}
                    >
                      PDF ↓
                    </a>
                  )}
                </div>
                <div className="mt-2 font-display font-bold text-slate-900">{s.name}</div>
                <div className="mt-1 text-[12.5px] text-slate-500">{s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
                {s.slug && <div className="mt-1 text-[11px] text-slate-500">Slug: <span className="font-mono">{s.slug}</span></div>}
                <div className="mt-1.5 text-[11px] text-slate-400">{s.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div></div>
              <div className="flex flex-col gap-1.5 lg:items-end">
                <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)}
                  className="px-3 py-1.5 rounded-full text-[12px] font-semibold border border-slate-200 bg-white focus:outline-none focus:border-trital-navy"
                  data-testid={`checklist-status-${s.id}`}>
                  <option value="pending_review">pending review</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                  <option value="needs_more_info">needs more info</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/*  NOTIFICATIONS QUEUE TAB                                        */
/* ============================================================== */
export function NotificationsTab() {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("");
  const [channelF, setChannelF] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF)  params.status  = statusF;
      if (channelF) params.channel = channelF;
      const r = await api.get("/admin/notifications", { params });
      setItems(r.data || []);
    } catch (e) { toast.error("Couldn't load notifications", { description: e?.response?.data?.detail || e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, channelF]);

  return (
    <div className="space-y-4" data-testid="admin-notifications-tab">
      <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-[12.5px] text-amber-800 inline-flex items-start gap-2" data-testid="notifications-banner">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
        <div><span className="font-semibold">Outbound delivery is queued.</span> Add Resend / Twilio API keys to drain the queue. Templates and routing are wired correctly — you can audit every queued send below.</div>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 shadow-card p-3 flex flex-wrap items-center gap-2">
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="notifications-filter-status">
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
        <select value={channelF} onChange={(e) => setChannelF(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="notifications-filter-channel">
          <option value="">All channels</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
          <option value="internal">Internal</option>
        </select>
        <span className="ml-auto text-[12px] text-slate-500">{items.length} notifications</span>
        <button onClick={() => downloadCSV(items, "notifications")} className="px-3 py-2 rounded-lg bg-trital-navy text-white text-[12.5px] font-semibold hover:bg-trital-navy-deep" data-testid="notifications-export-csv">
          Export CSV
        </button>
      </div>
      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No notifications in this view.</div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Channel</th>
                <th className="text-left px-5 py-3">To</th>
                <th className="text-left px-5 py-3">Template</th>
                <th className="text-left px-5 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((n) => (
                <tr key={n.id} className="hover:bg-slate-50" data-testid={`notification-row-${n.id}`}>
                  <td className="px-5 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[n.status] || ""}`}>{n.status}</span></td>
                  <td className="px-5 py-3"><span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider">{n.channel}</span></td>
                  <td className="px-5 py-3 text-slate-700 truncate max-w-[260px]">{Array.isArray(n.to) ? n.to.join(", ") : n.to}</td>
                  <td className="px-5 py-3 text-slate-600 font-mono text-[11.5px]">{n.template}</td>
                  <td className="px-5 py-3 text-[12px] text-slate-500">{n.created_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/*  AUDIT LOGS TAB                                                 */
/* ============================================================== */
export function AuditLogsTab() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (type) params.target_type = type;
      const r = await api.get("/admin/audit-logs", { params });
      setItems(r.data || []);
    } catch (e) { toast.error("Couldn't load audit logs", { description: e?.response?.data?.detail || e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  return (
    <div className="space-y-4" data-testid="admin-audit-tab">
      <div className="rounded-2xl bg-white border border-slate-200 shadow-card p-3 flex flex-wrap items-center gap-2">
        <select value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="audit-filter-type">
          <option value="">All target types</option>
          <option value="application">Application</option>
          <option value="lead">Lead</option>
          <option value="recruiter_task">Task</option>
          <option value="staffing_request">Staffing request</option>
          <option value="checklist_submission">Checklist</option>
          <option value="user">User</option>
          <option value="credential">Credential</option>
        </select>
        <span className="ml-auto text-[12px] text-slate-500">{items.length} log entries</span>
      </div>
      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No audit log entries yet. Status mutations across the platform will surface here automatically.</div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Action</th>
                <th className="text-left px-5 py-3">Target</th>
                <th className="text-left px-5 py-3">Actor</th>
                <th className="text-left px-5 py-3">After</th>
                <th className="text-left px-5 py-3">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((a) => (
                <tr key={a.id} data-testid={`audit-row-${a.id}`}>
                  <td className="px-5 py-3 font-mono text-[12px] text-trital-navy">{a.action}</td>
                  <td className="px-5 py-3 text-slate-600 text-[12px]">{a.target_type}/{a.target_id?.slice(0, 8)}</td>
                  <td className="px-5 py-3 text-slate-600 text-[12px]">{a.actor_email}</td>
                  <td className="px-5 py-3 text-slate-600 font-mono text-[11px]">{a.after ? JSON.stringify(a.after) : "—"}</td>
                  <td className="px-5 py-3 text-[11.5px] text-slate-500">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/*  OVERVIEW CARDS                                                 */
/* ============================================================== */
export function OverviewCards({ data }) {
  if (!data) return null;
  const cards = [
    { Icon: Inbox,        k: "new_clinician_leads",       label: "New clinician leads",     color: "navy" },
    { Icon: AlertCircle,  k: "new_facility_requests",     label: "New facility requests",   color: "blue" },
    { Icon: Bell,         k: "open_prn_applications",     label: "Open PRN applications",   color: "red" },
    { Icon: ShieldCheck,  k: "pending_checklist_reviews", label: "Pending checklist reviews", color: "navy" },
    { Icon: FileText,     k: "compliance_follow_ups",     label: "Compliance follow-ups",   color: "green" },
    { Icon: Activity,     k: "urgent_staffing_requests",  label: "Urgent staffing requests",color: "red" },
  ];
  const palette = {
    navy:  "bg-trital-medical-blue-soft text-trital-navy",
    blue:  "bg-blue-50 text-blue-700",
    red:   "bg-red-50 text-trital-red",
    green: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="overview-cards">
      {cards.map((c, i) => (
        <motion.div
          key={c.k}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: i * 0.04 }}
          className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card"
          data-testid={`overview-${c.k}`}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${palette[c.color]}`}><c.Icon className="w-5 h-5" strokeWidth={1.8} /></div>
          <div className="mt-3 font-display font-black text-3xl text-slate-900 tabular-nums">{data[c.k] ?? 0}</div>
          <div className="overline text-slate-500 mt-1">{c.label}</div>
        </motion.div>
      ))}
    </div>
  );
}
